import React, { useEffect, useState } from "react";
import { Button, Container, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { TbMoneybag } from "react-icons/tb";
import { GiReceiveMoney } from "react-icons/gi";
import apiCall from "../../API/API";
import "./IncomeSetting.css";

const LEVEL_FIELDS = [
  { key: "name", label: "Name", type: "text" },
  { key: "income", label: "Income", type: "text" },
  { key: "salary", label: "Salary", type: "number" },
  { key: "salary_duration", label: "Salary Duration", type: "text" },
  { key: "salary_days", label: "Salary Days", type: "number" },
  { key: "reward_amount", label: "Reward Amount", type: "number" },
  { key: "direct_required", label: "Direct Required", type: "number" },
  { key: "five_directs_required", label: "Five Directs Required", type: "number" },
  { key: "total_team_required", label: "Total Team Required", type: "number" },
];

const toFieldValue = (value) => {
  if (Array.isArray(value)) return value.join(", ");
  if (value && typeof value === "object") return JSON.stringify(value);
  if (value === null || value === undefined) return "";
  return value;
};

const parseField = (key, raw, incomeIsList) => {
  if (raw === "" || raw === null || raw === undefined) return undefined;
  const text = String(raw).trim();
  if (text === "") return undefined;

  if (key === "name") return text;

  if (key === "income" && (incomeIsList || text.startsWith("[") || text.includes(","))) {
    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    }
    return text
      .split(",")
      .map((part) => Number(part.trim()))
      .filter((part) => !Number.isNaN(part));
  }

  if (key === "salary_duration" && Number.isNaN(Number(text))) return text;

  if (text.startsWith("{") || text.startsWith("[")) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  const asNumber = Number(text);
  return Number.isNaN(asNumber) ? text : asNumber;
};

const extractPlan = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  if (
    payload.roi_income ||
    payload.direct_income ||
    payload.level_income_condition
  ) {
    return payload;
  }
  if (payload.data) return extractPlan(payload.data);
  if (payload.plan) return extractPlan(payload.plan);
  if (Array.isArray(payload.packages) && payload.packages[0]) {
    return extractPlan(payload.packages[0]);
  }
  return null;
};

const emptyRoi = {
  status: "1",
  income_from: "",
  income_to: "",
  in_day: "",
};

const emptyDirect = {
  status: "1",
  income: "",
};

const Field = ({ label, name, value, onChange, type = "text" }) => (
  <label className="income-setting__field">
    <span>{label}</span>
    {type === "status" ? (
      <select
        className="form-select"
        name={name}
        value={value}
        onChange={onChange}
      >
        <option value="1">Active</option>
        <option value="0">Inactive</option>
      </select>
    ) : (
      <input
        className="form-control"
        type={type}
        name={name}
        value={value}
        step={type === "number" ? "any" : undefined}
        onChange={onChange}
      />
    )}
  </label>
);

const IncomeSetting = () => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState("");
  const [roi, setRoi] = useState(emptyRoi);
  const [direct, setDirect] = useState(emptyDirect);
  const [levels, setLevels] = useState([]);
  const [editLevel, setEditLevel] = useState(null);

  const applyPlan = (plan) => {
    const roiIncome = plan?.roi_income || {};
    const directIncome = plan?.direct_income || {};
    const conditions = Array.isArray(plan?.level_income_condition)
      ? plan.level_income_condition
      : [];

    setRoi({
      status: String(roiIncome.status ?? 1),
      income_from: toFieldValue(roiIncome.income_from),
      income_to: toFieldValue(roiIncome.income_to),
      in_day: toFieldValue(roiIncome.in_day),
    });
    setDirect({
      status: String(directIncome.status ?? 1),
      income: toFieldValue(directIncome.income),
    });
    setLevels(
      conditions.map((item) => ({
        level: item.level,
        incomeIsList: Array.isArray(item.income),
        fields: {
          name: toFieldValue(item.name),
          income: toFieldValue(item.income),
          salary: toFieldValue(item.salary),
          salary_duration: toFieldValue(item.salary_duration),
          salary_days: toFieldValue(item.salary_days),
          reward_amount: toFieldValue(item.reward_amount),
          direct_required: toFieldValue(item.direct_required),
          five_directs_required: toFieldValue(item.five_directs_required),
          total_team_required: toFieldValue(item.total_team_required),
        },
      }))
    );
  };

  const fetchPlan = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setLoadError("");
    const { data, error } = await apiCall("POST", "/get-plan-id", { planId: 1 });
    const plan = extractPlan(data);
    if (error || !plan) {
      setLoadError(error?.message || "Could not load the current plan.");
      setLoading(false);
      return;
    }
    applyPlan(plan);
    setLoading(false);
  };

  useEffect(() => {
    fetchPlan();
  }, []);

  const onRoiChange = (event) => {
    const { name, value } = event.target;
    setRoi((prev) => ({ ...prev, [name]: value }));
  };

  const onDirectChange = (event) => {
    const { name, value } = event.target;
    setDirect((prev) => ({ ...prev, [name]: value }));
  };

  const openEdit = (index) => {
    const item = levels[index];
    setEditLevel({
      index,
      level: item.level,
      incomeIsList: item.incomeIsList,
      fields: { ...item.fields },
    });
  };

  const closeEdit = () => {
    if (saving.startsWith("level-")) return;
    setEditLevel(null);
  };

  const onEditChange = (event) => {
    const { name, value } = event.target;
    setEditLevel((prev) =>
      prev ? { ...prev, fields: { ...prev.fields, [name]: value } } : prev
    );
  };

  const saveRoi = async (event) => {
    event.preventDefault();
    const incomeFrom = Number(roi.income_from);
    const incomeTo = Number(roi.income_to);
    const inDay = Number(roi.in_day);
    const status = Number(roi.status);

    if (!status || roi.income_from === "" || roi.income_to === "" || roi.in_day === "") {
      toast.error("All fields are required");
      return;
    }

    setSaving("roi");
    const { error } = await apiCall(
      "POST",
      "/update-roi-income",
      {
        status,
        income_from: incomeFrom,
        income_to: incomeTo,
        in_day: inDay,
      },
      true
    );
    setSaving("");
    if (!error) fetchPlan();
  };

  const saveDirect = async (event) => {
    event.preventDefault();
    const status = Number(direct.status);
    const income = Number(direct.income);

    if (!status || direct.income === "") {
      toast.error("All fields are required");
      return;
    }

    setSaving("direct");
    const { error } = await apiCall(
      "POST",
      "/update-direct-income",
      { status, income },
      true
    );
    setSaving("");
    if (!error) fetchPlan();
  };

  const saveLevel = async (event) => {
    event.preventDefault();
    if (!editLevel) return;
    if (editLevel.level === undefined || editLevel.level === null || editLevel.level === "") {
      toast.error("Level is required");
      return;
    }

    const payload = { level: Number(editLevel.level) };
    Object.entries(editLevel.fields).forEach(([key, value]) => {
      const parsed = parseField(key, value, editLevel.incomeIsList);
      if (parsed !== undefined) payload[key] = parsed;
    });

    setSaving(`level-${editLevel.level}`);
    const { data, error } = await apiCall(
      "POST",
      "/update-level-income",
      payload,
      true
    );
    setSaving("");
    if (!error) {
      const updated = data?.data;
      if (updated && updated.level !== undefined) {
        setLevels((prev) =>
          prev.map((row) =>
            Number(row.level) === Number(updated.level)
              ? {
                  ...row,
                  incomeIsList: Array.isArray(updated.income),
                  fields: {
                    name: toFieldValue(updated.name),
                    income: toFieldValue(updated.income),
                    salary: toFieldValue(updated.salary),
                    salary_duration: toFieldValue(updated.salary_duration),
                    salary_days: toFieldValue(updated.salary_days),
                    reward_amount: toFieldValue(updated.reward_amount),
                    direct_required: toFieldValue(updated.direct_required),
                    five_directs_required: toFieldValue(updated.five_directs_required),
                    total_team_required: toFieldValue(updated.total_team_required),
                  },
                }
              : row
          )
        );
      } else {
        fetchPlan({ silent: true });
      }
      setEditLevel(null);
    }
  };

  const showCell = (value) => (value === "" || value === null || value === undefined ? "—" : value);
  const levelSaving = editLevel ? saving === `level-${editLevel.level}` : false;

  return (
    <Container fluid className="income-setting">
      <header className="income-setting__head">
        <h1>Income Setting</h1>
        <p>Update ROI, direct, and level income for the current plan.</p>
      </header>

      {loading ? (
        <div className="income-setting__status">Loading income settings...</div>
      ) : null}
      {loadError ? (
        <div className="income-setting__status">{loadError}</div>
      ) : null}

      <div className="income-setting__grid">
        <article className="income-card">
          <div className="income-card__header">
            <div className="income-card__title">
              <span className="income-card__icon">
                <GiReceiveMoney />
              </span>
              <div>
                <h3>ROI Income</h3>
                <p>Daily return range</p>
              </div>
            </div>
            <span className={`income-card__badge ${Number(roi.status) === 1 ? "is-on" : "is-off"}`}>
              {Number(roi.status) === 1 ? "Active" : "Inactive"}
            </span>
          </div>
          <form className="income-card__body" onSubmit={saveRoi}>
            <div className="income-card__fields">
              <Field label="Status" name="status" type="status" value={roi.status} onChange={onRoiChange} />
              <Field label="Income From" name="income_from" type="number" value={roi.income_from} onChange={onRoiChange} />
              <Field label="Income To" name="income_to" type="number" value={roi.income_to} onChange={onRoiChange} />
              <Field label="In Days" name="in_day" type="number" value={roi.in_day} onChange={onRoiChange} />
            </div>
            <div className="income-card__actions">
              <Button type="submit" variant="primary" disabled={saving === "roi"}>
                {saving === "roi" ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </article>

        <article className="income-card">
          <div className="income-card__header">
            <div className="income-card__title">
              <span className="income-card__icon">
                <TbMoneybag />
              </span>
              <div>
                <h3>Direct Income</h3>
                <p>Direct referral income</p>
              </div>
            </div>
            <span className={`income-card__badge ${Number(direct.status) === 1 ? "is-on" : "is-off"}`}>
              {Number(direct.status) === 1 ? "Active" : "Inactive"}
            </span>
          </div>
          <form className="income-card__body" onSubmit={saveDirect}>
            <div className="income-card__fields">
              <Field label="Status" name="status" type="status" value={direct.status} onChange={onDirectChange} />
              <Field label="Income" name="income" type="number" value={direct.income} onChange={onDirectChange} />
            </div>
            <div className="income-card__actions">
              <Button type="submit" variant="primary" disabled={saving === "direct"}>
                {saving === "direct" ? "Updating..." : "Update"}
              </Button>
            </div>
          </form>
        </article>
      </div>

      <section className="income-setting__section">
        <h2>Level Income</h2>
        {levels.length === 0 ? (
          <div className="income-setting__empty">
            {loading ? "Loading levels..." : "No level income conditions found."}
          </div>
        ) : (
          <div className="income-level-table">
            <table>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Name</th>
                  <th>Income</th>
                  <th>Salary</th>
                  <th>Salary Duration</th>
                  <th>Salary Days</th>
                  <th>Reward Amount</th>
                  <th>Direct Required</th>
                  <th>Five Directs Required</th>
                  <th>Total Team Required</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((item, index) => (
                  <tr key={`${item.level}-${index}`}>
                    <td>{showCell(item.level)}</td>
                    <td>{showCell(item.fields.name)}</td>
                    <td>{showCell(item.fields.income)}</td>
                    <td>{showCell(item.fields.salary)}</td>
                    <td>{showCell(item.fields.salary_duration)}</td>
                    <td>{showCell(item.fields.salary_days)}</td>
                    <td>{showCell(item.fields.reward_amount)}</td>
                    <td>{showCell(item.fields.direct_required)}</td>
                    <td>{showCell(item.fields.five_directs_required)}</td>
                    <td>{showCell(item.fields.total_team_required)}</td>
                    <td>
                      <Button variant="primary" size="sm" onClick={() => openEdit(index)}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Modal
        show={Boolean(editLevel)}
        onHide={closeEdit}
        size="lg"
        centered
        className="income-level-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit {editLevel?.fields?.name || `Level ${editLevel?.level ?? ""}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLevel ? (
            <form id="level-income-form" onSubmit={saveLevel}>
              <p className="income-level-modal__level">Level {editLevel.level}</p>
              <div className="income-card__fields">
                {LEVEL_FIELDS.map((field) => (
                  <Field
                    key={field.key}
                    label={field.label}
                    name={field.key}
                    type={field.key === "income" && editLevel.incomeIsList ? "text" : field.type}
                    value={editLevel.fields[field.key]}
                    onChange={onEditChange}
                  />
                ))}
              </div>
            </form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={levelSaving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="level-income-form"
            disabled={levelSaving}
          >
            {levelSaving ? "Updating..." : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default IncomeSetting;
